import React, { useEffect, useState } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { firestore, auth } from './FirebaseConfig';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
  const [data, setData] = useState([]);
  const [editableStatus, setEditableStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const snapshot = await getDocs(collection(firestore, 'loanApplications'));
      const fetchedData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setData(fetchedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}'s application?`)) {
      try {
        await deleteDoc(doc(firestore, 'loanApplications', id));
        fetchData();
      } catch (error) {
        console.error('Error deleting data:', error);
      }
    }
  };

  const handleUpdate = async (id) => {
    if (window.confirm('Are you sure you want to save changes?')) {
      try {
        await updateDoc(doc(firestore, 'loanApplications', id), { 
          status: editableStatus[id] 
        });
        
        setEditableStatus(prev => {
          const newState = {...prev};
          delete newState[id];
          return newState;
        });
        fetchData();
      } catch (error) {
        console.error('Error updating data:', error);
      }
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      signOut(auth)
        .then(() => {
          navigate('/admin-login');
        })
        .catch((error) => {
          console.error('Error signing out:', error);
        });
    }
  };

  // Filter and search functionality
  const filteredData = data.filter(item => {
    const matchesSearch = 
      item.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.applicationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.phone?.includes(searchTerm);
    
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="h3 mb-1 text-dark">Loan Applications Dashboard</h2>
              <p className="text-muted mb-0">Manage all loan applications in one place</p>
            </div>
            <button 
              className="btn btn-outline-danger"
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-2"></i>Logout
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">Total Applications</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{data.length}</div>
                </div>
                <div className="col-auto">
                  <i className="bi bi-files text-gray-300" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">Approved</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {data.filter(item => item.status === 'Approved').length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="bi bi-check-circle text-gray-300" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">Pending</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {data.filter(item => item.status === 'Pending').length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="bi bi-clock text-gray-300" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-danger shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">Rejected</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {data.filter(item => item.status === 'Rejected').length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="bi bi-x-circle text-gray-300" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, application no, email or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select 
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Processing">Processing</option>
          </select>
        </div>
        <div className="col-md-3">
          <button className="btn btn-outline-secondary w-100" onClick={fetchData}>
            <i className="bi bi-arrow-clockwise me-2"></i>Refresh
          </button>
        </div>
      </div>

      {/* Applications Table */}
      <div className="card shadow-lg border-0">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0">Loan Applications</h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive" style={{ maxHeight: '60vh' }}>
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Application No</th>
                  <th>Name</th>
                  <th>Loan Type</th>
                  <th>Loan Amount</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Applied On</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length > 0 ? (
                  sortedData.map((item, index) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{index + 1}</td>
                      <td>
                        <span className="badge bg-info text-dark">{item.applicationNumber}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" 
                               style={{ width: '36px', height: '36px' }}>
                            <span className="text-white fw-bold">{item.fullName?.charAt(0) || 'U'}</span>
                          </div>
                          <div>
                            <div className="fw-bold">{item.fullName}</div>
                            <small className="text-muted">{item.dob}</small>
                          </div>
                        </div>
                      </td>
                      <td>{item.loanPurpose}</td>
                      <td className="fw-bold text-success">{formatCurrency(item.loanAmount)}</td>
                      <td>
                        <a href={`tel:${item.phone}`} className="text-decoration-none">
                          {item.phone}
                        </a>
                      </td>
                      <td>
                        <a href={`mailto:${item.email}`} className="text-decoration-none">
                          {item.email}
                        </a>
                      </td>
                      <td>
                        {editableStatus[item.id] !== undefined ? (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editableStatus[item.id]}
                            onChange={(e) => setEditableStatus({
                              ...editableStatus,
                              [item.id]: e.target.value
                            })}
                            placeholder="Enter custom status"
                          />
                        ) : (
                          <span className={`badge ${
                            item.status === 'Approved' ? 'bg-success' :
                            item.status === 'Rejected' ? 'bg-danger' :
                            item.status === 'Processing' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {item.status || 'Pending'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="text-nowrap">
                          {item.dateTime || 'N/A'}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="btn-group" role="group">
                          {editableStatus[item.id] === undefined ? (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setEditableStatus({
                                  ...editableStatus,
                                  [item.id]: item.status || 'Pending'
                                })}
                                title="Edit status"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => setSelectedApplication(item)}
                                title="View details"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(item.id, item.fullName)}
                                title="Delete application"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleUpdate(item.id)}
                                title="Save changes"
                              >
                                <i className="bi bi-check"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  const newEditableStatus = { ...editableStatus };
                                  delete newEditableStatus[item.id];
                                  setEditableStatus(newEditableStatus);
                                }}
                                title="Cancel edit"
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      {data.length === 0 ? 'No loan applications found.' : 'No matching applications found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white d-flex justify-content-between align-items-center">
          <small className="text-muted">
            Showing {sortedData.length} of {data.length} applications
          </small>
          <small className="text-muted">
            Last updated: {new Date().toLocaleTimeString()}
          </small>
        </div>
      </div>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Application Details</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedApplication(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Personal Information</h6>
                    <p><strong>Name:</strong> {selectedApplication.fullName}</p>
                    <p><strong>DOB:</strong> {selectedApplication.dob}</p>
                    <p><strong>Email:</strong> {selectedApplication.email}</p>
                    <p><strong>Phone:</strong> {selectedApplication.phone}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>Loan Details</h6>
                    <p><strong>Application No:</strong> {selectedApplication.applicationNumber}</p>
                    <p><strong>Loan Type:</strong> {selectedApplication.loanPurpose}</p>
                    <p><strong>Loan Amount:</strong> {formatCurrency(selectedApplication.loanAmount)}</p>
                    <p><strong>Monthly Income:</strong> {formatCurrency(selectedApplication.monthlyIncome)}</p>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <h6>Document Details</h6>
                    <p><strong>PAN:</strong> {selectedApplication.panCard}</p>
                    <p><strong>Aadhaar:</strong> {selectedApplication.aadhaarCard}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>Address Details</h6>
                    <p><strong>City:</strong> {selectedApplication.employmentType}</p>
                    <p><strong>Pincode:</strong> {selectedApplication.address}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedApplication(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;